/**
 * Hook for managing the "While You Were Away" daily briefing.
 * Fetches data progressively: countries → (1s) → forms → (1s) → changes.
 * LLM summary fires in the background immediately after countries arrive.
 */

import { useState, useCallback } from 'react';
import { getFirebaseAuth } from '../firebase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BriefingCountry {
  country: string;
  submission_count: number;
}

export interface BriefingForm {
  country: string;
  form_id: number;
  form_title: string;
  submission_count: number;
}

export interface BriefingChange {
  country: string;
  question_label: string;
  question_order: number;
  recent_sentiment: number;
  previous_sentiment: number;
  sentiment_diff: number;
}

export interface BriefingPrefs {
  /** When set, skip `/briefing/countries` and use this list verbatim.
   *  Cap of 3 is enforced upstream by the Preferences modal and the
   *  server sanitizer; the hook trusts whatever it's handed. */
  countries?: string[];
  /** Which sections to actually fetch. Omitting a section skips its
   *  BigQuery call entirely — not just its render — to save cost. */
  sections?: {
    summary?: boolean;
    premise?: boolean;
    events?: boolean;
  };
}

export interface UseBriefingReturn {
  shouldShow: boolean;
  loading: boolean;
  countries: BriefingCountry[] | null;
  forms: BriefingForm[] | null;
  changes: BriefingChange[] | null;
  aiSummary: string | null;
  error: string | null;
  checkComplete: boolean;
  checkBriefingStatus: () => Promise<void>;
  fetchBriefing: (prefs?: BriefingPrefs) => Promise<void>;
  dismissBriefing: () => Promise<void>;
}

// ── Auth helper ──────────────────────────────────────────────────────────────

async function getAuthToken(): Promise<string> {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  return user ? await user.getIdToken() : '';
}

function delay(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBriefing(): UseBriefingReturn {
  const [shouldShow, setShouldShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<BriefingCountry[] | null>(null);
  const [forms, setForms] = useState<BriefingForm[] | null>(null);
  const [changes, setChanges] = useState<BriefingChange[] | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkComplete, setCheckComplete] = useState(false);

  const checkBriefingStatus = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) { setCheckComplete(true); return; }

      const res = await fetch('/api/user/briefing-status', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      const { lastBriefingDate } = await res.json();

      const today = new Date().toISOString().split('T')[0];
      if (!lastBriefingDate || lastBriefingDate < today) {
        setShouldShow(true);
      }
    } catch (err) {
      console.error('[briefing] Status check failed:', err);
    } finally {
      setCheckComplete(true);
    }
  }, []);

  const fetchBriefing = useCallback(async (prefs?: BriefingPrefs) => {
    setLoading(true);
    setError(null);
    const want = {
      summary: prefs?.sections?.summary !== false,
      premise: prefs?.sections?.premise !== false,
      events:  prefs?.sections?.events  !== false,
    };
    try {
      const token = await getAuthToken();
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };

      // 1. Country list — either the user's pinned focus list from the
      //    Preferences modal, or the auto-ranked top countries from the
      //    server. The pinned path short-circuits the server fetch
      //    entirely; we fabricate submission_count=0 placeholders so the
      //    downstream UI layout stays the same.
      let countryList: BriefingCountry[];
      if (prefs?.countries && prefs.countries.length > 0) {
        countryList = prefs.countries.map((c) => ({ country: c, submission_count: 0 }));
        setCountries(countryList);
      } else {
        const countriesRes = await fetch('/api/briefing/countries', { headers, cache: 'no-store' });
        const countriesPayload = await countriesRes.json();
        if (countriesPayload.skipped) {
          setShouldShow(false);
          return;
        }
        countryList = countriesPayload.countries;
        setCountries(countryList);
      }
      const countryNames = countryList.map(c => c.country);

      // 2. Fire off LLM summary in background when the user wants it.
      if (want.summary) {
        fetch('/api/briefing/summary', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ countries: countryNames }),
        })
          .then(r => r.json())
          .then(d => setAiSummary(d.aiSummary || ''))
          .catch(() => setAiSummary('Summary unavailable.'));
      } else {
        setAiSummary(null);
      }

      // 3. Premise results — forms + change deltas. One of the two
      //    section toggles ("premise") drives both since they share a
      //    render block in the modal.
      if (want.premise) {
        await delay(1000);
        const formsRes = await fetch(
          `/api/briefing/forms?countries=${encodeURIComponent(countryNames.join(','))}`,
          { headers, cache: 'no-store' },
        );
        const formsPayload = await formsRes.json();
        const formList: BriefingForm[] = formsPayload.forms || [];
        setForms(formList);

        const formIds = [...new Set(formList.map(f => f.form_id))];

        await delay(1000);
        const changesRes = await fetch(
          `/api/briefing/changes?formIds=${formIds.join(',')}&countries=${encodeURIComponent(countryNames.join(','))}`,
          { headers, cache: 'no-store' },
        );
        const changesPayload = await changesRes.json();
        setChanges(changesPayload.changes);
      } else {
        setForms([]);
        setChanges([]);
      }
      // `want.events` currently has no dedicated endpoint — the AI
      // summary IS the events digest. When a separate scraped-event
      // list is added, it'll gate on want.events the same way.
    } catch (err: any) {
      console.error('[briefing] Fetch failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const dismissBriefing = useCallback(async () => {
    setShouldShow(false);
    try {
      const token = await getAuthToken();
      if (!token) return;

      await fetch('/api/user/briefing-dismiss', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      console.error('[briefing] Dismiss failed:', err);
    }
  }, []);

  return {
    shouldShow, loading, countries, forms, changes, aiSummary, error, checkComplete,
    checkBriefingStatus, fetchBriefing, dismissBriefing,
  };
}
